import React, { FC, useMemo, useEffect, useState } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import useStyles from './styles'
import { BaseLayout } from 'components'
import { useOktaAuth } from '@okta/okta-react'
import { routes, api } from 'config'
import { constants } from 'utils'
import ButtonWIthCustomCSS from 'components/custom-button/custom-button-style'
import { useFlagsmith } from 'flagsmith/react'
import useCheckMultiLoginUser from 'hooks/useCheckMultiLoginUser'

const MFA_ENROLLED_KEY = 'mfa_group_enrolled'

const PasswordAndSecurity: FC = () => {
  const { classes } = useStyles()
  const { oktaAuth, authState } = useOktaAuth()
  const flagsmith = useFlagsmith()
  const [isMfaEnrollmentEnabled, setIsMfaEnrollmentEnabled] = useState(false)
  const [isEnrolling, setIsEnrolling] = useState(false)
  const [enrollError, setEnrollError] = useState<string | null>(null)
  const [enrollSuccess, setEnrollSuccess] = useState(() => {
    return sessionStorage.getItem(MFA_ENROLLED_KEY) === 'true'
  })

  const isMultiLoginUser = useCheckMultiLoginUser()

  // Identify user with Flagsmith for user-specific flags
  useEffect(() => {
    const identifyUser = async () => {
      if (!authState?.idToken?.idToken || !flagsmith.initialised) return

      try {
        const { payload } = oktaAuth.token.decode(authState.idToken.idToken)
        if (payload.memberMlsId) {
          await flagsmith.identify(`member:${payload.memberMlsId}`)
          // Update flag state after identify completes
          setIsMfaEnrollmentEnabled(
            flagsmith.hasFeature(constants.FEATURE_FLAGS.MFA_ENROLLMENT),
          )
        }
      } catch (error) {
        console.error('Error identifying user with Flagsmith:', error)
      }
    }

    identifyUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState, oktaAuth, flagsmith.initialised])

  const isMFARequired = useMemo(() => {
    if (!authState?.accessToken?.accessToken) return false
    const { payload } = oktaAuth.token.decode(authState.accessToken.accessToken)
    return payload.groups?.includes(constants.OKTA_GROUPS.MFA_REQUIRED) ?? false
  }, [authState, oktaAuth])

  // Clear sessionStorage flag once token reflects MFA group membership (after re-login)
  useEffect(() => {
    if (isMFARequired) {
      sessionStorage.removeItem(MFA_ENROLLED_KEY)
    }
  }, [isMFARequired])

  const handleEnrollMfa = async () => {
    if (!authState?.accessToken?.accessToken) return

    setIsEnrolling(true)
    setEnrollError(null)

    try {
      const response = await fetch(api.enrollMfaGroup(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authState.accessToken.accessToken}`,
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to enroll in MFA')
      }

      setEnrollSuccess(true)
      sessionStorage.setItem(MFA_ENROLLED_KEY, 'true')
    } catch (error) {
      setEnrollError(
        error instanceof Error ? error.message : 'Failed to enroll in MFA',
      )
    } finally {
      setIsEnrolling(false)
    }
  }

  const actionSection = () => {
    const sectionItems = [
      {
        active: true,
        headLine: 'Account Password',
        summaryText:
          'Manage your account password you used to sign in that is unique to this account.',
        buttonCTA: 'Change Password',
        buttonAction: routes.changePassword,
        newTab: false,
      },
      {
        active: isMultiLoginUser,
        headLine: 'Multi Login Password',
        summaryText:
          'Manage your Multi Login account password you used to sign in that is unique to this account.',
        buttonCTA: 'Change Password',
        buttonAction: routes.multiLoginChangePassword,
        newTab: false,
      },
      {
        active: isMFARequired,
        headLine: 'Two-Factor Authentication',
        summaryText:
          "For added security, we'll send a verification code to ensure that only you can access the information you store in BrightMLS.",
        buttonCTA: 'Update Enrollment',
        buttonAction: routes.manageMfa,
        newTab: false,
      },
    ]
    return sectionItems
      .filter((item) => item.active)
      .map((item) => (
        <Box key={item.headLine} className={classes.outlining}>
          <Box className={classes.textView}>
            <Typography className={classes.headLine}>
              {item.headLine}
            </Typography>
            <Typography className={classes.summaryText}>
              {item.summaryText}
            </Typography>
          </Box>
          <Box className={classes.buttonView}>
            <ButtonWIthCustomCSS
              color="primary"
              variant="contained"
              href={item.buttonAction}
              target={item.newTab ? '_blank' : undefined}
              rel={item.newTab ? 'noopener noreferrer' : undefined}
              type="submit"
              style={{ boxShadow: 'unset', textTransform: 'none' }}
            >
              {item.buttonCTA}
            </ButtonWIthCustomCSS>
          </Box>
        </Box>
      ))
  }

  const enrollSection = () => {
    if (isMFARequired || !isMfaEnrollmentEnabled) return null

    if (enrollSuccess) {
      return (
        <Box className={classes.outlining}>
          <Box className={classes.textView}>
            <Typography className={classes.headLine}>
              Two-Factor Authentication
            </Typography>
            <Typography
              style={{
                color: '#2e7d32',
                fontSize: '14px',
                marginBottom: '8px',
              }}
            >
              Successfully enrolled in two-factor authentication.
            </Typography>
            <Typography className={classes.summaryText}>
              Click "Update Enrollment" to set up your verification methods.
            </Typography>
          </Box>
          <Box className={classes.buttonView}>
            <ButtonWIthCustomCSS
              color="primary"
              variant="contained"
              href={routes.manageMfa}
              style={{ boxShadow: 'unset', textTransform: 'none' }}
            >
              Update Enrollment
            </ButtonWIthCustomCSS>
          </Box>
        </Box>
      )
    }

    return (
      <Box className={classes.outlining}>
        <Box className={classes.textView}>
          <Typography className={classes.headLine}>
            Two-Factor Authentication
          </Typography>
          <Typography className={classes.summaryText}>
            Enhance your account security by enabling two-factor authentication.
            Once enrolled, you&apos;ll receive a verification code to confirm
            your identity when signing in.
          </Typography>
          {enrollError && (
            <Typography style={{ color: '#d32f2f', fontSize: '14px' }}>
              {enrollError}
            </Typography>
          )}
        </Box>
        <Box className={classes.buttonView}>
          <ButtonWIthCustomCSS
            color="primary"
            variant="contained"
            onClick={handleEnrollMfa}
            disabled={isEnrolling}
            style={{ boxShadow: 'unset', textTransform: 'none' }}
          >
            {isEnrolling ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Enable Two-Factor'
            )}
          </ButtonWIthCustomCSS>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <BaseLayout />
      <Box className={classes.moduleSpace}>
        <Box className={classes.outlineContainer}>
          <Typography className={classes.title}>Password & Security</Typography>
          {actionSection()}
          {enrollSection()}
        </Box>
      </Box>
    </>
  )
}

export default PasswordAndSecurity
